"use client";

import { useState } from "react";
import { Share, RefreshCw, ArrowRight } from "lucide-react";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import { Button } from "@/components/ui/button";
import { ShareModal, type SocialPlatform } from "@/components/share-modal";

interface ImpactActionsProps {
  projectName: string;
  projectSlug?: string;
}

const SOCIAL_PLATFORMS: SocialPlatform[] = [
  { name: "WhatsApp", icon: "message-circle", color: "bg-green-500", utmSource: "whatsapp" },
  { name: "Facebook", icon: "facebook", color: "bg-blue-600", utmSource: "facebook" },
  { name: "Twitter", icon: "twitter", color: "bg-blue-400", utmSource: "twitter" },
  { name: "Instagram", icon: "instagram", color: "bg-pink-500", utmSource: "instagram" },
  { name: "E-Mail", icon: "mail", color: "bg-gray-600", utmSource: "email" },
];

export function ImpactActions({ projectName, projectSlug }: ImpactActionsProps) {
  const t = useTranslations("impact");
  const [isShareOpen, setIsShareOpen] = useState(false);

  const projectUrl = projectSlug
    ? `${typeof window !== "undefined" ? window.location.origin : ""}/projects/${projectSlug}`
    : typeof window !== "undefined" ? window.location.href : "";

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 w-full">
      {projectSlug && (
        <Button asChild className="w-full">
          <Link href={`/projects/${projectSlug}`}>
            <ArrowRight className="h-4 w-4 mr-2" />
            {t("dashboard.viewDetails")}
          </Link>
        </Button>
      )}
      <Button
        variant="outline"
        onClick={() => setIsShareOpen(true)}
        className="w-full bg-black/5 dark:bg-black/30 backdrop-blur-sm border-white/15 dark:border-white/10 hover:bg-black/10 dark:hover:bg-black/40"
      >
        <Share className="h-4 w-4 mr-2" />
        {t("actions.share")}
      </Button>
      <Button variant="outline" asChild
        className="w-full bg-black/5 dark:bg-black/30 backdrop-blur-sm border-white/15 dark:border-white/10 hover:bg-black/10 dark:hover:bg-black/40"
      >
        <Link href="/projects">
          <RefreshCw className="h-4 w-4 mr-2" />
          {t("actions.changeProject")}
        </Link>
      </Button>

      <ShareModal
        isOpen={isShareOpen}
        onClose={() => setIsShareOpen(false)}
        title={t("actions.share")}
        description={projectName}
        linkLabel={t("actions.share")}
        linkPlaceholder={projectUrl}
        socialPlatforms={SOCIAL_PLATFORMS}
        shareMessage={projectName}
        utmCampaign="25-08_share-project"
      />
    </div>
  );
}
